// ===========================================
// Invoices API Functions
// ===========================================
// API calls for invoice and payment management endpoints

import { apiClient } from '../services/api';
import type { RegistrationCreditOutcome } from '../utils/registrationCreditOutcome';
import type { DiscountCoverage } from '../types/discount.types';

// ===========================================
// INVOICE PAYMENT REMINDERS (2026-09-29)
// ===========================================
// ⚠️ Copied field by field from apps/backend/src/services/invoiceReminders/manual.ts
// (ReminderHistoryRow, ReminderHistory, ManualSendResult; slice B4). A response type here is ASSERTED,
// never validated (CLAUDE.md) — keep the two in step.

export type ReminderStepKind = 'BEFORE_DUE' | 'ON_DUE' | 'LAST_CHANCE' | 'OVERDUE';

export type InvoiceReminderBlockedReason =
  | 'DRAFT'
  | 'NOT_OWED'
  | 'STRIPE_MANAGED'
  | 'CHECKOUT_LIVE'
  | 'ALREADY_SENT'
  | 'NOTHING_DUE'
  | 'STEP_DISABLED'
  | 'STEP_CHANGED'
  // #372: "Reminders on hold".
  | 'ON_HOLD';

export interface InvoiceReminderRow {
  id: string;
  /** v1 = a legacy ledger row with no v2 twin. */
  source: 'v2' | 'v1';
  /** The step's name when it went (v2), or a server-made label for the old step (v1). */
  stepName: string;
  /** null for v1 rows. */
  kind: ReminderStepKind | null;
  /** v2: 0 = the first send, n = the nth repeat. */
  occurrence: number | null;
  /** v2: the school day it was scheduled for (YYYY-MM-DD). */
  scheduledFor: string | null;
  /** The due date it was for (YYYY-MM-DD). */
  dueDate: string | null;
  trigger: 'SWEEP' | 'MANUAL';
  triggeredByName: string | null;
  claimedAt: string;
  sentAt: string | null;
  skippedAt: string | null;
  skipReason: string | null;
  failedAt: string | null;
  failureReason: string | null;
  recipientCount: number;
  copySentTo: string | null;
}

export interface InvoiceReminderNextManual {
  stepId: string;
  stepName: string;
  kind: ReminderStepKind;
  occurrence: number;
}

export interface InvoiceReminderHistory {
  rows: InvoiceReminderRow[];
  /** The invoice's current due date (YYYY-MM-DD). */
  dueDate: string;
  nextManual: InvoiceReminderNextManual | null;
  nextManualBlockedReason: InvoiceReminderBlockedReason | null;
  nextManualBlockedMessage: string | null;
  /** Is the automatic sweep live on the server? */
  remindersActive: boolean;
}

export interface InvoiceReminderSendResult {
  step: InvoiceReminderNextManual;
  status: 'SENT' | 'NO_RECIPIENTS' | 'FAILED';
  recipientCount: number;
  accepted: number;
  copy: 'SENT' | 'FAILED' | null;
  reason?: string;
}

export interface InvoiceReminderSendInput {
  expectedStepId: string;
  expectedOccurrence: number;
}

// ===========================================
// TYPES
// ===========================================

export type InvoiceStatus =
  | 'DRAFT'
  | 'SENT'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'REFUNDED';

export type PaymentMethod =
  | 'STRIPE'
  | 'BANK_TRANSFER'
  | 'CASH'
  | 'CHECK'
  | 'OTHER'
  // #642: credit applied from the family's account (not money received). Written only by the server.
  | 'ACCOUNT_CREDIT';

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  pricingPackageId: string | null;
  /**
   * Product catalog link. Present when the line came from a Product (either the
   * Products-page bulk flow or the invoice page's "Add product" button).
   *
   * ⚠️ Any edit path that rebuilds line items MUST carry this through:
   * `updateInvoice` deletes and recreates every item, so dropping the field
   * silently NULLs the FK and severs the line from product reporting and
   * inventory history. It was absent from this interface until 2026-09-24,
   * which is why `EditInvoiceDialog` could drop it with no type error.
   */
  productId: string | null;
  description: string;
  quantity: number;
  unitPrice: string;
  total: string;
  createdAt: string;
  /**
   * #372: what the line is for (decides which invoices a late fee charges). `kindLocked` lines
   * were made by the system: the server keeps their kind whatever is sent. ⚠️ Every edit path
   * that rebuilds lines MUST carry `id` and `kind` (like productId): line ids are stable.
   */
  kind: InvoiceLineKind;
  kindLocked: boolean;
  /**
   * #625 D3: the product's category when the line was written (a snapshot, no FK). What a
   * discount's coverage is judged on; null for a non-product or uncategorised line.
   */
  productCategoryId: string | null;
}

export type InvoiceLineKind = 'TUITION' | 'PRODUCT' | 'OTHER';

/**
 * #372: one charged late fee. A waived fee keeps its row (shown struck through); it counts
 * towards nothing. `waiverReason` / `notifiedAt` are ADMIN responses only (parent routes
 * allow-list the fields); `waivedByName` reaches the OWNER tier only (absent for other staff).
 */
export interface InvoiceLateFee {
  id: string;
  sequence: number;
  amount: string;
  basis: 'FIXED' | 'PERCENT';
  rate: string | null;
  capAmount: string | null;
  cappedBy: 'PER_FEE' | 'TOTAL' | null;
  taxable: boolean;
  appliedAt: string;
  /** YYYY-MM-DD (a @db.Date). */
  scheduledFor: string;
  dueDateAtCharge: string;
  waivedAt: string | null;
  notifiedAt?: string | null;
  waivedByName?: string | null;
  waiverReason?: string | null;
}

/** #642 Phase 2 Request 7: why a payment needs checking against the bank. */
export type PaymentCheckReason = 'AMOUNT_DIFFERS' | 'NOT_IN_BANK' | 'DUPLICATE' | 'OTHER';

/** One open "Needs checking" flag (GET /invoices/admin/payments/needs-checking, owner only). */
export interface PaymentNeedingCheck {
  id: string;
  amount: string;
  method: PaymentMethod;
  paidAt: string;
  reason: PaymentCheckReason;
  note: string | null;
  setAt: string;
  setByName: string | null;
  invoice: { id: string; invoiceNumber: string };
  familyName: string;
}

export interface Payment {
  id: string;
  invoiceId: string;
  amount: string;
  /** #210: a passed-on card fee charged on top of `amount`; null when none. */
  processingFee: string | null;
  method: PaymentMethod;
  stripePaymentId: string | null;
  reference: string | null;
  notes: string | null;
  paidAt: string;
  createdAt: string;
  /**
   * #642: payments are VOIDED, never deleted. Staff reads only (a family never sees a voided row
   * or these fields); `voidedByName` is the owner's only (null for everyone else).
   */
  voidedAt?: string | null;
  voidedByName?: string | null;
  voidReason?: string | null;
  /**
   * #642 Phase 2 Request 7: "Needs checking", the owner's flag (staff reads only; never a family).
   * Open = `needsCheckingAt` set and `checkResolvedAt` null. The names are the owner's only.
   */
  needsCheckingAt?: string | null;
  needsCheckingReason?: PaymentCheckReason | null;
  needsCheckingNote?: string | null;
  needsCheckingByName?: string | null;
  checkResolvedAt?: string | null;
  checkResolutionNote?: string | null;
  checkResolvedByName?: string | null;
  /**
   * #642 Phase 2 (family views only): the part of this payment that went to the family's account
   * credit, "170.00", derived by the server. Staff read FamilyCreditEntry instead.
   */
  toCredit?: string;
  /** #642: the row's account-credit movements (a live ADDED = the part that went to credit). */
  FamilyCreditEntry?: Array<{ id: string; kind: 'ADDED' | 'APPLIED' | 'REFUND_DUE' | 'REFUNDED'; amount: string; voidedAt: string | null }>;
  invoice?: {
    id: string;
    invoiceNumber: string;
    description: string | null;
  };
}

export interface InvoiceFamily {
  id: string;
  name: string;
  Parent: Array<{
    id: string;
    contact1FirstName: string;
    contact1LastName: string;
    contact1Email: string;
    contact1Phone: string | null;
  }>;
  Student: Array<{
    id: string;
    firstName: string;
    lastName: string;
  }>;
}

export interface InvoiceTerm {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface InvoiceDiscount {
  id: string;
  invoiceId: string;
  discountType: string;
  scope: string;
  name: string;
  description: string | null;
  value: string;
  isPercent: boolean;
  calculatedAmount: string;
  baseAmount: string;
  approvedAt: string | null;
  approvedByAdminId: string | null;
  createdAt: string;
  updatedAt: string;
  /** #625 D4: what it covers, as stored on the row. */
  coversTuition: boolean;
  coversOther: boolean;
  coversAllProducts: boolean;
  coversUncategorised: boolean;
  coversCategoryIds: string[];
  /** #625 D9: the words, from the invoice READS (absent on a mutation's response). */
  coverageLabel?: string;
}

export interface Invoice {
  id: string;
  schoolId: string;
  familyId: string;
  termId: string | null;
  invoiceNumber: string;
  description: string | null;
  subtotal: string;
  tax: string;
  total: string;
  amountPaid: string;
  status: InvoiceStatus;
  dueDate: string;
  sentAt: string | null;
  paidAt: string | null;
  /** Set when the last send attempt delivered to nobody; cleared by a successful send. */
  lastSendFailedAt?: string | null;
  lastSendFailureReason?: string | null;
  /** Amend-in-place (2026-09-26): how many times this sent invoice was amended. */
  amendedCount?: number;
  lastAmendedAt?: string | null;
  /** Stamped by the first payment, never cleared. */
  firstPaymentAt?: string | null;
  /** The live Stripe checkout for this invoice; amend is locked while checkoutExpiresAt is in the future. */
  checkoutStartedAt?: string | null;
  checkoutExpiresAt?: string | null;
  /**
   * Void (2026-09-30). `cancelledAt` is an INSTANT (render in local time). The reason and
   * who are ADMIN responses only: parent routes strip them (the reason is an internal tax
   * record). All NULL on invoices voided before reasons were kept.
   */
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  cancelledByName?: string | null;
  /** Set only on Stripe-owned subscription invoices (never amendable). */
  stripeInvoiceId?: string | null;
  /** PARENT responses only: the latest amendment's date + the school's family-facing reason. */
  latestAmendment?: InvoiceLatestAmendment | null;
  createdAt: string;
  updatedAt: string;
  Family: InvoiceFamily;
  Term: InvoiceTerm | null;
  InvoiceItem: InvoiceItem[];
  Payment: Payment[];
  InvoiceDiscount?: InvoiceDiscount[];
  /** ADMIN detail only (#376): open "not billed" notices. Never on a parent response. */
  notBilledNotices?: NotBilledNoticeView[];
  /** ADMIN list only (#376): how many open "not billed" notices this invoice has. */
  openNotBilledCount?: number;
  /** #372: every fee row, waived included, in order. */
  InvoiceLateFee?: InvoiceLateFee[];
  /**
   * #372: the summary row(s) for ACTIVE fees, computed by the SERVER's one resolver (the PDF's),
   * so the page never needs the school's tax mode. `beforeTax` = print between Discounts and Tax.
   */
  lateFeeRows?: Array<{ label: string; amount: number; beforeTax: boolean }>;
  /** #372 "Reminders on hold" (ADMIN responses only; parent routes strip them). */
  remindersOnHoldAt?: string | null;
  remindersOnHoldByName?: string | null;
  remindersOnHoldNote?: string | null;
}

/** #372: what the waive's email did (the waive stands either way). */
export interface LateFeeNotification {
  requested: boolean;
  delivered: boolean;
  sentTo: string[];
  failed: string[];
  reason?: 'NO_RECIPIENTS' | 'ALL_REJECTED' | 'SEND_ERROR' | 'TEMPLATE_SWITCHED_OFF';
}

/** #372 slice C: the invoice page's "next late fee" line. Mirrors backend services/lateFees/preview.ts. */
export type LateFeePreviewBlockReason =
  | 'ON_HOLD'
  | 'FAMILY_EXEMPT'
  | 'KIND_NOT_CHARGED'
  | 'REPEATS_OFF'
  | 'AT_MAX_COUNT'
  | 'AT_CAP'
  | 'BELOW_MIN_OWING'
  | 'ROUNDS_TO_ZERO'
  | 'CHECKOUT_PENDING';

export type LateFeePreview =
  | { state: 'NONE' }
  | { state: 'WAITING_LAST_CHANCE' }
  /** `date` is a school-day key (YYYY-MM-DD); `amount` a dollar string. */
  | { state: 'NEXT'; sequence: number; date: string; amount: string; dueToday: boolean }
  | { state: 'BLOCKED'; reason: LateFeePreviewBlockReason; sequence: number; date: string };

/** #372: the hold columns after a hold or release. */
export interface RemindersHold {
  remindersOnHoldAt: string | null;
  remindersOnHoldByName: string | null;
  remindersOnHoldNote: string | null;
}

export interface InvoiceLatestAmendment {
  amendedAt: string;
  reason: string;
}

/** Backend `InvoiceSnapshot` (services/invoice/amend/snapshot.ts). Money is a 2dp string. */
export interface InvoiceSnapshot {
  description: string | null;
  /** YYYY-MM-DD (the @db.Date day). */
  dueDate: string;
  status: InvoiceStatus;
  subtotal: string;
  tax: string;
  totalDiscounts: string;
  total: string;
  amountPaid: string;
  paidAt: string | null;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: string;
    total: string;
    productId: string | null;
    pricingPackageId: string | null;
    /** #372 (snapshots before 2026-10-15 lack it). */
    kind?: InvoiceLineKind;
  }>;
  /** #372: Σ active late fees (absent when there are none, and on older snapshots). */
  lateFees?: string;
  /** `id` is the InvoiceDiscount row (snapshots before 2026-09-28 lack it). */
  /** `coverageLabel`: #625, what it covered at the time (snapshots before #625 lack it). */
  discounts: Array<{ id?: string; name: string; value: string; isPercent: boolean; calculatedAmount: string; coverageLabel?: string }>;
}

export interface AmendmentNotifyResult {
  sentTo?: string[];
  failed?: string[];
  attemptedAt?: string;
  resend?: boolean;
  reason?: string;
  skipped?: 'ADMIN_CHOSE_NOT_TO_NOTIFY';
}

/** Backend `InvoiceAmendmentDTO`. */
export interface InvoiceAmendment {
  id: string;
  invoiceId: string;
  amendmentNumber: number;
  reason: string;
  before: InvoiceSnapshot;
  after: InvoiceSnapshot;
  performedById: string | null;
  performedByName: string;
  notifiedAt: string | null;
  notifyResult: AmendmentNotifyResult | null;
  checkoutCancelled: boolean;
  createdAt: string;
}

export interface AmendmentNotification {
  requested: boolean;
  delivered: boolean;
  sentTo: string[];
  failed: string[];
  reason?: 'NO_RECIPIENTS' | 'ALL_REJECTED' | 'SEND_ERROR' | 'ADMIN_CHOSE_NOT_TO_NOTIFY';
}

/** POST /cancel ("Void"): the reason is required and internal; the email is opt-in. */
export interface VoidInvoiceInput {
  reason: string;
  notifyFamily: boolean;
}

/** Same shape as the amendment notice outcome (backend `VoidNotification`). */
export type VoidNotification = AmendmentNotification;

export type VoidInvoiceResult = Invoice & { notification: VoidNotification };

export interface AmendInvoiceInput {
  description?: string | null;
  dueDate?: string;
  items: CreateInvoiceItemInput[];
  /** Required and FAMILY-FACING. */
  reason: string;
  notifyParent: boolean;
  /** Admin override: expire the family's live Stripe checkout, then amend. */
  cancelCheckout?: boolean;
  /** The amendedCount the form was built from; a mismatch is 409 STALE_INVOICE. */
  expectedAmendedCount?: number;
  /**
   * A one-off discount applied as part of the amendment (2026-09-28) — the draft
   * one-off's own shape. Needs operations_pricing:write server-side.
   */
  discount?: {
    name: string;
    value: number;
    isPercent: boolean;
    saveToCustomDiscounts?: boolean;
    /** #625: absent = the school default. */
    coverage?: DiscountCoverage;
  };
  /** Existing discounts on this invoice to remove. Needs operations_pricing:delete. */
  removeDiscountIds?: string[];
}

/** The amendment's money inputs only — what the live-totals preview needs. */
export type AmendPreviewInput = Omit<AmendInvoiceInput, 'reason' | 'notifyParent' | 'cancelCheckout'>;

/** Backend `AmendPreview`: what saving WOULD do. Nothing is saved by asking. */
export interface AmendPreview {
  before: InvoiceSnapshot;
  after: InvoiceSnapshot;
  /** The form currently matches the invoice (a save would be refused). */
  noChanges: boolean;
}

export interface AmendInvoiceResult {
  invoice: Invoice;
  amendment: InvoiceAmendment;
  notification: AmendmentNotification;
}

export interface InvoiceFilters {
  familyId?: string;
  termId?: string;
  status?: InvoiceStatus;
  dueDateFrom?: string;
  dueDateTo?: string;
}

export interface CreateInvoiceItemInput {
  description: string;
  quantity: number;
  unitPrice: number;
  pricingPackageId?: string;
  /** Product catalog link — see the warning on `InvoiceItem.productId`. */
  productId?: string;
  /** #372: the line's id on an edit (kept by the server: stable line ids). */
  id?: string;
  /** #372: the admin's kind for a typed line (ignored on a locked line). */
  kind?: InvoiceLineKind;
}

export interface AddProductToInvoiceInput {
  productId: string;
  quantity: number;
  /** Omit to use the product's catalogue price (resolved server-side). */
  unitPrice?: number;
}

export interface CreateInvoiceInput {
  familyId: string;
  termId?: string;
  description?: string;
  dueDate: string;
  items: CreateInvoiceItemInput[];
}

/**
 * The DRAFT edit (`PATCH /invoices/admin/invoices/:id`). Field for field the backend
 * `updateInvoiceSchema` — this type is an assertion, not a check, so keep the two in
 * step. The discount fields (2026-10-02) are saved WITH the edit, in one transaction.
 */
export interface UpdateInvoiceInput {
  description?: string | null;
  dueDate?: string;
  items?: CreateInvoiceItemInput[];
  /** Saved/eligible discount ids from the discount preview. Needs operations_pricing:write. */
  applyDiscountIds?: string[];
  /** A one-off discount — the amend's shape. Needs operations_pricing:write. */
  discount?: AmendInvoiceInput['discount'];
  /** Discounts on this invoice to remove. Needs operations_pricing:delete. */
  removeDiscountIds?: string[];
  /** Internal note for the removal audit row; the server has a default. */
  removeDiscountReason?: string;
  /** #374: children whose Held registration fee comes off this draft. Needs registration_fees:write. */
  addRegistrationCreditStudentIds?: string[];
}

/** The draft edit's money inputs — what its live-totals preview needs. */
export type EditPreviewInput = Omit<UpdateInvoiceInput, 'removeDiscountReason'>;

export interface RecordPaymentInput {
  amount: number;
  method: Exclude<PaymentMethod, 'STRIPE' | 'ACCOUNT_CREDIT'>;
  reference?: string;
  notes?: string;
  /** School-local day the money was received (YYYY-MM-DD); omitted = today. */
  paidAt?: string;
  /** #642 D1: what happens to an amount above the balance (required when there is one). */
  excessAction?: 'CREDIT' | 'REFUND_DUE';
  /** #642: the Refund due's note. */
  excessNote?: string;
}

/**
 * Amend a manual payment's reference / notes (#642 D4). '' clears. The amount, method and
 * date are never edited: void the payment and record it again.
 */
export interface UpdatePaymentInput {
  reference?: string;
  notes?: string;
}

/** Per-family discount picks sent with a run. Mirrors the backend `DiscountSelections`. */
export interface GenerateDiscountSelections {
  [familyId: string]: {
    familyDiscount?: boolean;
    customDiscounts?: string[];
    studentDiscounts?: {
      [studentId: string]: {
        instrumentDiscount?: boolean;
        programLevelDiscount?: boolean;
        customDiscounts?: string[];
      };
    };
  };
}

export interface GenerateTermInvoiceInput {
  termId: string;
  familyIds?: string[];
  dueDate?: string;
  groupRate?: number;
  individualRate?: number;
  standardLessonRate?: number;
  discountSelections?: GenerateDiscountSelections;
}

// ===========================================
// Auto Generate: bill what can be billed, Check first (#376)
// ===========================================
// Mirror the backend shapes in services/invoice/billing/bulkRunReport.ts FIELD BY FIELD: a response
// type here is an assertion, never a check (CLAUDE.md "A frontend response type is ASSERTED").

/** One price to fix, and the families it holds back. Backend `BulkFix` (= `MissingPrice` + families). */
export interface BulkFix {
  lessonId: string;
  lessonName: string;
  programLevelName: string | null;
  durationMinutes: number;
  category: 'INDIVIDUAL' | 'GROUP' | string;
  zeroPrice?: boolean;
  noProgramLevel?: boolean;
  hint?: string;
  familyIds: string[];
  familyNames: string[];
}

/** A family held back as a whole because one of its classes lacks a price. Backend `BlockedFamily`. */
export interface BlockedFamily {
  familyId: string;
  familyName: string;
  students: Array<{ studentName: string; lessonName: string; cell: string; fixKey: string }>;
}

export interface AlreadyInvoicedFamily {
  familyId: string;
  familyName: string;
  invoiceNumbers: string[];
}

export interface FamilyRef {
  familyId: string;
  familyName: string;
}

export type ManualReason =
  | 'TO_CHECK'
  | 'STILL_BLOCKED'
  | 'DISCOUNT_FAILED'
  | 'REGISTRATION_CREDIT_FAILED'
  | 'NOT_BILLED_NOTICE';

/** #374: one registration fee credit a run applied (or, in Check first, would apply). Backend RegistrationCreditLine. */
export interface RegistrationCreditLine {
  familyId: string;
  familyName: string;
  studentId: string;
  studentName: string;
  amount: string;
  invoiceId?: string;
  invoiceNumber?: string;
}

/** Backend RegistrationCreditsReport. */
export interface RegistrationCreditsReport {
  applied: RegistrationCreditLine[];
  skipped: Array<{ familyId: string; familyName: string; studentName: string; reason: string; invoiceNumber?: string }>;
}

/** "Do these by hand" (the asterisk). Backend `ManualEntry`. */
export interface ManualEntry {
  familyId: string;
  familyName: string;
  reason: ManualReason;
  detail: string;
  remedy: string;
  invoiceId?: string;
  invoiceNumber?: string;
  enrollmentId?: string;
  notBilledReason?: NotBilledNoticeReason;
}

/** Check first's answer (200): what Generate would do, nothing created. Backend `BulkCheckResult`. */
export interface BulkCheckResult {
  termId: string;
  termName: string;
  willCreate: FamilyRef[];
  nothingToBill: FamilyRef[];
  errors: Array<{ familyId: string; error: string }>;
  notBilled: NotBilledEnrollment[];
  blocked: BlockedFamily[];
  fixes: BulkFix[];
  manual: ManualEntry[];
  alreadyInvoiced: AlreadyInvoicedFamily[];
  /** #374: the credits Generate would apply. Optional on READ so an older server's answer still renders. */
  registrationCredits?: RegistrationCreditsReport;
}

/** Why an invoice billed a linked enrolment nothing, or left its band weeks off (#376 D5). */
export type NotBilledNoticeReason = 'NO_WEEKS' | 'BAND_TBD';

/** One open "not billed" notice on an invoice (admin only). Backend `NotBilledNoticeView`. */
export interface NotBilledNoticeView {
  /** The link id: what Mark as handled names. */
  id: string;
  enrollmentId: string;
  studentId: string;
  studentName: string;
  lessonId: string;
  lessonName: string;
  reason: NotBilledNoticeReason;
  /** The cause in plain words, stored when the invoice was made. */
  note: string;
  /** BAND_TBD only: the class's active band NOW, read live (null = still none). */
  bandNow: { name: string } | null;
}

/** A child the run did not bill because they are marked Inactive or removed (#190). Mirrors the
 *  backend `NotBilledEnrollment` (`services/invoice/billing/billableEnrollments.ts`). */
export interface NotBilledEnrollment {
  enrollmentId: string;
  familyId: string;
  familyName: string;
  studentId: string;
  studentName: string;
  lessonName: string;
  reason: 'STUDENT_INACTIVE' | 'STUDENT_REMOVED';
}

/** What Auto Generate returns (201). Backend `BulkRunResult`. The #376 lists are optional on READ so
 *  an older server's answer still renders. */
export interface GenerateInvoicesResult {
  created: Invoice[];
  /** Real failures only. */
  errors: Array<{ familyId: string; error: string }>;
  notBilled: NotBilledEnrollment[];
  nothingToBill?: FamilyRef[];
  blocked?: BlockedFamily[];
  fixes?: BulkFix[];
  manual?: ManualEntry[];
  alreadyInvoiced?: AlreadyInvoicedFamily[];
  /** #374: the registration fee credits this run applied. */
  registrationCredits?: RegistrationCreditsReport;
}

// ===========================================
// Billable Context (manual-invoice week selection)
// ===========================================
// Mirrors the backend BillableContext shape (services/invoice/invoice.types.ts).

export type BillableBucketKind = 'STANDARD' | 'GROUP' | 'INDIVIDUAL' | 'BAND';
export type HybridCombination = 'GROUP_INDIVIDUAL' | 'GROUP_BAND' | 'INDIVIDUAL_BAND';

export interface BillableBucket {
  kind: BillableBucketKind;
  /** Weeks the lesson actually runs for this bucket — seeds the WeekTilePicker. */
  sessionWeeks: number[];
  /** Per-week matrix rate; null when the matrix has no entry (manual entry required). */
  unitPrice: number | null;
  /**
   * Weekday (0=Sun…6=Sat) this bucket's sessions fall on, null if unscheduled —
   * the class's day, or the linked BAND lesson's day for a hybrid band bucket.
   * Dates a mid-term start ("start date dd/mm/yyyy").
   */
  dayOfWeek: number | null;
}

export interface BillableEnrollment {
  enrollmentId: string;
  lessonId: string;
  lessonName: string;
  studentId: string;
  studentName: string;
  programLevelId: string | null;
  programLevelName: string;
  instrumentId: string | null;
  instrumentName: string | null;
  lessonType: string;
  hybridCombination: HybridCombination | null;
  /** The week THIS student joins (`LessonEnrollment.startWeek`); null = the class's own start. */
  startWeek: number | null;
  /** The class's own first / last term week (`Lesson.startWeek` / `endWeek`); null = term start / end. */
  classStartWeek: number | null;
  classEndWeek: number | null;
  buckets: BillableBucket[];
  /** #376: the class bills band weeks but has no active band. Present only then. */
  bandTbd?: 'NO_BAND' | 'INACTIVE_BAND';
}

/** One bucket of a manual week selection as sent to the server: weeks, never a price. */
export interface WeekSelectionBucketPayload {
  kind: BillableBucketKind;
  weeks: number[];
  /** Only when the admin switched "Custom price" on; absent = the server's matrix price. */
  customRate?: number;
}

/** One enrollment row of a manual week selection as sent to the server. */
export interface WeekSelectionPayload {
  enrollmentId: string;
  startWeek: number | null;
  buckets: WeekSelectionBucketPayload[];
}

export interface WeekInvoicePreviewInput {
  familyId: string;
  termId: string;
  selections: WeekSelectionPayload[];
}

export interface CreateInvoiceFromWeeksInput extends WeekInvoicePreviewInput {
  dueDate: string;
  description?: string;
}

/**
 * The SERVER's pricing of a week selection — the exact lines it will create,
 * priced from the pricing matrix (or an explicit custom rate) and worded as the
 * family will read them. Mirrors backend `PricedWeekSelections`.
 */
export interface PricedWeekSelections {
  grouping: 'BY_CLASS' | 'BY_PROGRAM_LEVEL';
  items: CreateInvoiceItemInput[];
  /** enrollmentId → the "start date dd/mm/yyyy" printed for it, or null. */
  startDates: Record<string, string | null>;
  total: number;
}

/** Why a sent invoice cannot be amended (mirrors backend `InvoiceLockReason`). */
export type InvoiceLockReason = 'PAYMENT_RECORDED' | 'CHECKOUT_LIVE' | 'SUBSCRIPTION' | 'PAID';

/**
 * A sent invoice that MAY need amending after a class window / student start-week change
 * (backend `findInvoicesToAmend`). "May": an invoice line carries no lesson id, so the server
 * cannot tell which invoice billed which class.
 */
export interface InvoiceToAmend {
  id: string;
  invoiceNumber: string;
  status: string;
  familyId: string;
  familyName: string;
  sentAt: string | null;
  amendable: boolean;
  lockReason?: InvoiceLockReason;
}

export interface BillableContext {
  termId: string;
  termName: string;
  termStartDate: string;
  termEndDate: string;
  termWeeks: number;
  enrollments: BillableEnrollment[];
}

/** What `sendAll` now returns: the queue receipt, NOT a delivery report. */
export interface QueuedSendResult {
  queued: number;
  message: string;
}

export interface BulkSendResult {
  sent: Array<{ id: string; invoiceNumber: string; familyName: string; sentTo: string[] }>;
  errors: Array<{ id: string; invoiceNumber: string; error: string }>;
}

/** GET /invoices/admin/invoices/default-due-date (#243). */
export interface DefaultDueDate {
  defaultDueDays: number;
  /** yyyy-mm-dd: the school's today + defaultDueDays. */
  defaultDueDate: string;
}

export interface InvoiceStatistics {
  totalOutstanding: number;
  totalOverdue: number;
  invoicesByStatus: Record<InvoiceStatus, number>;
  recentPayments: Payment[];
}

export interface PricingPackageItem {
  type: 'lesson' | 'addon' | 'material';
  name: string;
  quantity?: number;
  lessonTypeId?: string;
  instrumentId?: string;
  price?: number;
}

export interface PricingPackage {
  id: string;
  schoolId: string;
  name: string;
  description: string | null;
  price: string;
  items: PricingPackageItem[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    invoiceItems: number;
  };
}

export interface CreatePricingPackageInput {
  name: string;
  description?: string;
  price: number;
  items: PricingPackageItem[];
}

export interface UpdatePricingPackageInput {
  name?: string;
  description?: string | null;
  price?: number;
  items?: PricingPackageItem[];
  isActive?: boolean;
}

export interface CheckoutSession {
  sessionId: string;
  url: string;
}

export interface BankTransferDetails {
  schoolName: string;
  bsb: string;
  accountNumber: string;
  accountName: string;
}

export interface VerifyPaymentResult {
  /** null when `not_found`: the session is not this family's live checkout (#273). */
  invoiceId: string | null;
  status: 'already_processed' | 'processed' | 'pending' | 'not_found';
}

// ===========================================
// API RESPONSE TYPE
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// ===========================================
// ADMIN - PRICING PACKAGES API
// ===========================================

export const pricingPackagesApi = {
  /**
   * Get all pricing packages
   */
  getAll: async (includeInactive = false): Promise<PricingPackage[]> => {
    const response = await apiClient.get<ApiResponse<PricingPackage[]>>(
      '/invoices/admin/pricing-packages',
      { params: { includeInactive: includeInactive.toString() } }
    );
    return response.data;
  },

  /**
   * Get a single pricing package
   */
  getById: async (id: string): Promise<PricingPackage> => {
    const response = await apiClient.get<ApiResponse<PricingPackage>>(
      `/invoices/admin/pricing-packages/${id}`
    );
    return response.data;
  },

  /**
   * Create a new pricing package
   */
  create: async (data: CreatePricingPackageInput): Promise<PricingPackage> => {
    const response = await apiClient.post<ApiResponse<PricingPackage>>(
      '/invoices/admin/pricing-packages',
      data
    );
    return response.data;
  },

  /**
   * Update a pricing package
   */
  update: async (
    id: string,
    data: UpdatePricingPackageInput
  ): Promise<PricingPackage> => {
    const response = await apiClient.patch<ApiResponse<PricingPackage>>(
      `/invoices/admin/pricing-packages/${id}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a pricing package
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/invoices/admin/pricing-packages/${id}`);
  },
};

// ===========================================
// ADMIN - INVOICES API
// ===========================================

export const adminInvoicesApi = {
  /**
   * Get all invoices with optional filters
   */
  getAll: async (filters?: InvoiceFilters): Promise<Invoice[]> => {
    const response = await apiClient.get<ApiResponse<Invoice[]>>(
      '/invoices/admin/invoices',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get invoice statistics for dashboard
   */
  getStatistics: async (): Promise<InvoiceStatistics> => {
    const response = await apiClient.get<ApiResponse<InvoiceStatistics>>(
      '/invoices/admin/invoices/statistics'
    );
    return response.data;
  },

  /**
   * The school's default due date for a new invoice: school today + Default Due Days (#243).
   * Computed by the server so the browser never recomputes the rule or uses its own day.
   */
  getDefaultDueDate: async (): Promise<DefaultDueDate> => {
    const response = await apiClient.get<ApiResponse<DefaultDueDate>>(
      '/invoices/admin/invoices/default-due-date'
    );
    return response.data;
  },

  /**
   * Get a single invoice with details
   */
  getById: async (id: string): Promise<Invoice> => {
    const response = await apiClient.get<ApiResponse<Invoice>>(
      `/invoices/admin/invoices/${id}`
    );
    return response.data;
  },

  /**
   * Amend a SENT/OVERDUE invoice in place (no payment recorded, no live checkout).
   */
  amend: async (id: string, data: AmendInvoiceInput): Promise<AmendInvoiceResult> => {
    const response = await apiClient.post<ApiResponse<AmendInvoiceResult>>(
      `/invoices/admin/invoices/${id}/amend`,
      data
    );
    return response.data;
  },

  /**
   * Live totals for the amend dialog: the server runs the real amendment and
   * rolls it back. Saves nothing.
   */
  previewAmend: async (id: string, data: AmendPreviewInput): Promise<AmendPreview> => {
    const response = await apiClient.post<ApiResponse<AmendPreview>>(
      `/invoices/admin/invoices/${id}/amend/preview`,
      data
    );
    return response.data;
  },

  /**
   * Live totals for the DRAFT Edit dialog (2026-10-02): the server runs the real
   * edit, rolls it back, and answers in the amend preview's shape.
   */
  previewEdit: async (id: string, data: EditPreviewInput): Promise<AmendPreview> => {
    const response = await apiClient.post<ApiResponse<AmendPreview>>(
      `/invoices/admin/invoices/${id}/edit/preview`,
      data
    );
    return response.data;
  },

  /**
   * The invoice's amendment history, newest first.
   */
  getAmendments: async (id: string): Promise<InvoiceAmendment[]> => {
    const response = await apiClient.get<ApiResponse<InvoiceAmendment[]>>(
      `/invoices/admin/invoices/${id}/amendments`
    );
    return response.data;
  },

  /**
   * The invoice's payment-reminder history (newest first) + what "Send reminder now" would send.
   */
  getReminders: async (id: string): Promise<InvoiceReminderHistory> => {
    const response = await apiClient.get<ApiResponse<InvoiceReminderHistory>>(
      `/invoices/admin/invoices/${id}/reminders`
    );
    return response.data;
  },

  /**
   * "Send reminder now": sends the step the admin confirmed (`expectedStepId` + `expectedOccurrence`);
   * 409 STEP_CHANGED if that is no longer the step due, 409 ON_HOLD while reminders are on hold.
   * 502/500 when nobody was reached (nothing recorded: a retry is safe).
   */
  sendReminder: async (id: string, input: InvoiceReminderSendInput): Promise<InvoiceReminderSendResult> => {
    const response = await apiClient.post<ApiResponse<InvoiceReminderSendResult>>(
      `/invoices/admin/invoices/${id}/reminders/send`,
      input
    );
    return response.data;
  },

  /**
   * Re-send the latest amendment's notice to the family.
   */
  resendAmendmentNotice: async (
    id: string,
    amendmentId: string
  ): Promise<{ amendment: InvoiceAmendment; notification: AmendmentNotification }> => {
    const response = await apiClient.post<
      ApiResponse<{ amendment: InvoiceAmendment; notification: AmendmentNotification }>
    >(`/invoices/admin/invoices/${id}/amendments/${amendmentId}/notify`);
    return response.data;
  },

  /**
   * Create a new invoice
   */
  create: async (data: CreateInvoiceInput): Promise<Invoice> => {
    const response = await apiClient.post<ApiResponse<Invoice>>(
      '/invoices/admin/invoices',
      data
    );
    return response.data;
  },

  /**
   * Get billable context (enrollments + per-bucket session weeks + matrix prices)
   * for a family + term. Read-only preview that drives the per-week selector.
   */
  getBillableContext: async (
    familyId: string,
    termId: string
  ): Promise<BillableContext> => {
    const response = await apiClient.get<ApiResponse<BillableContext>>(
      '/invoices/admin/invoices/billable-context',
      { params: { familyId, termId } }
    );
    return response.data;
  },

  /**
   * Price a manual week selection on the server (writes nothing). The server
   * prices from the pricing matrix; the body carries weeks, never prices.
   */
  previewWeekSelections: async (input: WeekInvoicePreviewInput): Promise<PricedWeekSelections> => {
    const response = await apiClient.post<ApiResponse<PricedWeekSelections>>(
      '/invoices/admin/invoices/billable-context/preview',
      input
    );
    return response.data;
  },

  /**
   * Create a DRAFT invoice from a manual week selection, priced by the server.
   */
  createFromWeeks: async (input: CreateInvoiceFromWeeksInput): Promise<Invoice & { registrationCredits?: RegistrationCreditOutcome }> => {
    const response = await apiClient.post<ApiResponse<Invoice & { registrationCredits?: RegistrationCreditOutcome }>>(
      '/invoices/admin/invoices/from-weeks',
      input
    );
    return response.data;
  },

  /**
   * Generate term invoices for families
   */
  generateTermInvoices: async (
    data: GenerateTermInvoiceInput
  ): Promise<GenerateInvoicesResult> => {
    const response = await apiClient.post<ApiResponse<GenerateInvoicesResult>>(
      '/invoices/admin/invoices/generate',
      data
    );
    // apiClient.post already returns res.data, so response IS the body { status, data }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body = response as any;
    return body.data ?? body;
  },

  /**
   * Check first (#376): what Auto Generate would do for this form. Creates nothing.
   */
  checkTermInvoices: async (data: GenerateTermInvoiceInput): Promise<BulkCheckResult> => {
    const response = await apiClient.post<ApiResponse<BulkCheckResult>>(
      '/invoices/admin/invoices/generate/check',
      data
    );
    return response.data;
  },

  /**
   * Mark one "not billed" notice as handled (#376, one-way). Returns the invoice's remaining notices.
   */
  markNotBilledHandled: async (invoiceId: string, linkId: string): Promise<NotBilledNoticeView[]> => {
    const response = await apiClient.post<ApiResponse<{ notBilledNotices: NotBilledNoticeView[] }>>(
      `/invoices/admin/invoices/${invoiceId}/not-billed/${linkId}/handled`,
      {}
    );
    return response.data.notBilledNotices;
  },

  /**
   * Update an invoice (only DRAFT invoices)
   */
  update: async (id: string, data: UpdateInvoiceInput): Promise<Invoice & { registrationCredits?: RegistrationCreditOutcome }> => {
    const response = await apiClient.patch<ApiResponse<Invoice & { registrationCredits?: RegistrationCreditOutcome }>>(
      `/invoices/admin/invoices/${id}`,
      data
    );
    return response.data;
  },

  /**
   * Add a catalogue product as a line item (only DRAFT invoices).
   * Server reads the price from the Product unless `unitPrice` overrides it, and
   * deducts stock for inventory-tracked products.
   */
  addProduct: async (id: string, data: AddProductToInvoiceInput): Promise<Invoice> => {
    const response = await apiClient.post<ApiResponse<Invoice>>(
      `/invoices/admin/invoices/${id}/items/product`,
      data
    );
    return response.data;
  },

  /**
   * Delete an invoice (only DRAFT invoices)
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/invoices/admin/invoices/${id}`);
  },

  /**
   * Send invoice to family (changes status to SENT)
   */
  send: async (id: string): Promise<Invoice> => {
    const response = await apiClient.post<ApiResponse<Invoice>>(
      `/invoices/admin/invoices/${id}/send`
    );
    return response.data;
  },

  /**
   * Queue all draft invoices to be sent.
   *
   * ⚠️ Returns what was QUEUED, not what was sent (2026-09-09). The send moved
   * to a background job because a full-term run is ~300 emails and the admin
   * was made to wait for all of them. Nothing here can report delivery.
   */
  sendAll: async (): Promise<QueuedSendResult> => {
    const response = await apiClient.post<ApiResponse<QueuedSendResult>>(
      `/invoices/admin/invoices/send-all`
    );
    return response.data;
  },

  /**
   * Cancel an invoice
   */
  /** Void an invoice (shown as VOID; stored as CANCELLED). */
  cancel: async (id: string, data: VoidInvoiceInput): Promise<VoidInvoiceResult> => {
    const response = await apiClient.post<ApiResponse<VoidInvoiceResult>>(
      `/invoices/admin/invoices/${id}/cancel`,
      data
    );
    return response.data;
  },

  /** #372: waive one late fee (staff-only reason). The family is emailed unless the System Email is off. */
  waiveLateFee: async (
    id: string,
    feeId: string,
    reason: string
  ): Promise<Invoice & { notification: LateFeeNotification }> => {
    const response = await apiClient.post<ApiResponse<Invoice & { notification: LateFeeNotification }>>(
      `/invoices/admin/invoices/${id}/late-fees/${feeId}/waive`,
      { reason }
    );
    return response.data;
  },

  /** #372: what the next late fee on this invoice will be (or why there is none). */
  getLateFeePreview: async (id: string): Promise<LateFeePreview> => {
    const response = await apiClient.get<ApiResponse<LateFeePreview>>(`/invoices/admin/invoices/${id}/late-fees/preview`);
    return response.data;
  },

  /** #372: put this invoice's reminders AND late fees on hold (staff-only note). */
  holdReminders: async (id: string, note: string): Promise<RemindersHold> => {
    const response = await apiClient.post<ApiResponse<RemindersHold>>(`/invoices/admin/invoices/${id}/reminders/hold`, {
      note,
    });
    return response.data;
  },

  /** #372: release the hold. */
  releaseReminders: async (id: string): Promise<RemindersHold> => {
    const response = await apiClient.post<ApiResponse<RemindersHold>>(
      `/invoices/admin/invoices/${id}/reminders/release`,
      {}
    );
    return response.data;
  },

  /**
   * Record a manual payment
   */
  recordPayment: async (
    id: string,
    data: RecordPaymentInput
  ): Promise<Payment> => {
    const response = await apiClient.post<ApiResponse<Payment>>(
      `/invoices/admin/invoices/${id}/payment`,
      data
    );
    return response.data;
  },

  /**
   * Amend a manually recorded payment (Stripe payments are refused server-side)
   */
  updatePayment: async (
    paymentId: string,
    data: UpdatePaymentInput
  ): Promise<Invoice> => {
    const response = await apiClient.patch<ApiResponse<Invoice>>(
      `/invoices/admin/payments/${paymentId}`,
      data
    );
    return response.data;
  },

  /**
   * Void a manually recorded payment, with a reason (#642 D4: payments are never deleted).
   * 409 CREDIT_IN_USE names what to undo first.
   */
  voidPayment: async (paymentId: string, reason: string): Promise<Invoice> => {
    const response = await apiClient.post<ApiResponse<Invoice>>(
      `/invoices/admin/payments/${paymentId}/void`,
      { reason }
    );
    return response.data;
  },

  /** #642 Phase 2 Request 7 (owner only): flag a payment as needing checking. */
  flagPaymentNeedsChecking: async (
    paymentId: string,
    body: { reason: PaymentCheckReason; note?: string | null }
  ): Promise<void> => {
    await apiClient.post(`/invoices/admin/payments/${paymentId}/needs-checking`, body);
  },

  /** #642 Phase 2 Request 7 (owner only): resolve the flag, with a note. */
  resolvePaymentCheck: async (paymentId: string, note: string, flaggedAt?: string | null): Promise<void> => {
    // `flaggedAt`: the flag on screen, so a stale dialog never resolves a newer one (409 FLAG_CHANGED).
    await apiClient.post(`/invoices/admin/payments/${paymentId}/needs-checking/resolve`, {
      note,
      ...(flaggedAt ? { flaggedAt } : {}),
    });
  },

  /** #642 Phase 2 Request 7 (owner only): every open flag, oldest first. */
  getPaymentsNeedingCheck: async (): Promise<PaymentNeedingCheck[]> => {
    const response = await apiClient.get<ApiResponse<PaymentNeedingCheck[]>>('/invoices/admin/payments/needs-checking');
    return Array.isArray(response.data) ? response.data : [];
  },
};

// ===========================================
// PARENT - INVOICES API
// ===========================================

/** #642 Phase 2 Request 6: the parent's own family credit (GET /invoices/parent/credit). */
export interface ParentFamilyCredit {
  balance: string;
  refundDue: string;
  termsLine: string;
  movements: Array<{
    kind: 'ADDED' | 'APPLIED' | 'REFUND_DUE' | 'REFUNDED';
    amount: string;
    /** YYYY-MM-DD, a school day. */
    occurredOn: string;
    invoiceNumber: string | null;
  }>;
}

export const parentInvoicesApi = {
  /** #642 Phase 2: the family's own account credit (the family comes from the session). */
  getCredit: async (): Promise<ParentFamilyCredit> => {
    const response = await apiClient.get<ApiResponse<ParentFamilyCredit>>('/invoices/parent/credit');
    return response.data;
  },

  /**
   * Get all invoices for the parent's family
   */
  getAll: async (): Promise<Invoice[]> => {
    const response = await apiClient.get<ApiResponse<Invoice[]>>(
      '/invoices/parent/invoices'
    );
    return response.data;
  },

  /**
   * Get a single invoice (must belong to parent's family)
   */
  getById: async (id: string): Promise<Invoice> => {
    const response = await apiClient.get<ApiResponse<Invoice>>(
      `/invoices/parent/invoices/${id}`
    );
    return response.data;
  },

  /**
   * Create a Stripe checkout session for invoice payment
   */
  createPaymentSession: async (
    id: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<CheckoutSession> => {
    const response = await apiClient.post<ApiResponse<CheckoutSession>>(
      `/invoices/parent/invoices/${id}/pay`,
      { successUrl, cancelUrl }
    );
    return response.data;
  },

  /**
   * The parent came back from Stripe without paying: retire the session so the
   * school can amend the invoice straight away. Best-effort; the lock also
   * lifts on its own when the session expires.
   */
  cancelCheckout: async (id: string): Promise<{ cleared: boolean }> => {
    const response = await apiClient.post<ApiResponse<{ cleared: boolean }>>(
      `/invoices/parent/invoices/${id}/checkout/cancel`
    );
    return response.data;
  },

  /**
   * Get payment history for the parent's family
   */
  getPaymentHistory: async (): Promise<Payment[]> => {
    const response = await apiClient.get<ApiResponse<Payment[]>>(
      '/invoices/parent/payments'
    );
    return response.data;
  },

  /**
   * Download invoice as PDF
   */
  downloadPdf: async (id: string, invoiceNumber: string): Promise<void> => {
    const response = await apiClient.get(`/invoices/parent/invoices/${id}/pdf`, {
      responseType: 'blob',
    });

    // Create download link
    const blob = new Blob([response as unknown as BlobPart], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Get bank transfer details for the school
   */
  getBankDetails: async (): Promise<BankTransferDetails | null> => {
    const response = await apiClient.get<ApiResponse<BankTransferDetails | null>>(
      '/invoices/parent/bank-details'
    );
    return response.data;
  },

  /**
   * Verify and process a Stripe checkout session for invoice payment
   */
  verifyPayment: async (sessionId: string): Promise<VerifyPaymentResult> => {
    const response = await apiClient.post<ApiResponse<VerifyPaymentResult>>(
      '/invoices/parent/invoices/verify-payment',
      { sessionId }
    );
    return response.data;
  },
};
